"use client";

import { CircleX, Mic, Search } from "lucide-react";
import { useEffect } from "react";
import { useAutocomplete } from "../../../../app/hooks/meilisearch/useAutocomplete";
import { useMeilisearchStore } from "../../../../app/stores/filter-store";
import { useTranscription } from "../../../../app/hooks/ai/useTranscription";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  PostHostEventsEnum,
  sendPostHogEvent,
} from "../../../../app/libs/post-hog/post-hog-send-event";
import { getCookie } from "cookies-next";
import { MeiliSearchVehicleType } from "../../../../app/types/Vehicle.types";
import { stringToSlug } from "../../../../app/utils/common";

const Hero = ({ image }: { image: string }) => {
  const { hits, selectedIndex, setSelectedIndex, handleKeyDown } =
    useAutocomplete();
  const { query, setQuery } = useMeilisearchStore();
  const {
    isRecording,
    transcription,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useTranscription();

  const pathname = usePathname();

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  useEffect(() => {
    if (transcription) {
      setQuery(transcription);
    }
  }, [transcription, setQuery]);

  const handlePostHogEvent = async (vehicle: MeiliSearchVehicleType) => {
    const email: string =
      ((await getCookie("leadEmail")) as string) ?? undefined;

    sendPostHogEvent({
      eventName: PostHostEventsEnum.USER_SEARCH_HOME_AUTOCOMPLETE,
      email: email,
      eventProperties: { ...vehicle },
    });
  };

  return (
    <section
      className={`custom-before before:-z-10 bg-primary-100 dark:bg-slate-700 pb-0  ${pathname === "/" || pathname === "/motos" ? "" : "md:mt-20"} pt-10 md:pt-20 relative`}
    >
      <div className="box-border">
        <div className="mx-auto my-0 px-4 sm:px-6 lg:px-8 w-full">
          <div className="m-auto p-0 w-full max-w-4xl text-center rounded-full">
            <span className="text-base leading-7 mb-2.5 block">
              {pathname === "/motos"
                ? "Encontre a sua próxima moto"
                : "Encontre o seu próximo veículo"}
            </span>
            <h2 className="mb-4 text-3xl lg:text-6xl tracking-tight font-bold leading-tight relative">
              {pathname === "/motos"
                ? "Sua nova Moto está aqui!"
                : "Seu novo carro está aqui!"}
            </h2>
            <div className="z-10">
              <div id="tab-1" className="-mb-5">
                <div className="middle flex flex-col justify-evenly items-center h-80 -mt-48">
                  <div className="w-full relative mt-52">
                    <span className="h-5 w-5 absolute mt-9 left-4 bottom-5">
                      <Search color="#9aa0a6" />
                    </span>

                    {isRecording && (
                      <button
                        className="absolute top-2 right-16 text-white bg-red-500 hover:bg-red-600 p-2 rounded-full"
                        onClick={cancelRecording}
                      >
                        <CircleX strokeWidth={1.5} />
                      </button>
                    )}
                    <span
                      className={`h-9 w-9 flex justify-center items-center rounded-full absolute mt-8 right-4 bottom-[10px] cursor-pointer ${isRecording ? "text-red-500 animate-mic-pulse shadow-xl" : "text-gray-500"}`}
                    >
                      <Mic
                        color={isRecording ? "#FF000099" : "#9aa0a6"}
                        onClick={handleMicClick}
                        strokeWidth={1.5}
                      />
                    </span>
                    <input
                      placeholder={
                        pathname.includes("/motos")
                          ? "Yahama MT-07"
                          : "Fiat Uno"
                      }
                      type="text"
                      className="h-14 w-full bg-white border border-gray-500 text-black pl-12 pr-20 py-3 rounded-full hover:bg-gray-50 outline-none"
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={handleKeyDown}
                      value={query}
                    />
                  </div>
                </div>
              </div>

              {hits && hits.length > 0 && (
                <div className="relative">
                  <div className="absolute z-50 w-full max-h-96 overflow-y-auto bg-white shadow-inner p-1 rounded-2xl overflow-hidden">
                    {hits.map((hit, index) => {
                      const isFirst = index === 0;
                      const isLast = index === hits.length - 1;
                      const detailsVehiclePath = stringToSlug(
                        `${hit.make}-${hit.model}-${hit.category ? hit.category : hit.style ? hit.style : ""}-${hit.transmission ?? hit.typeStart}-${hit.fuel}-${hit.modelYear}-${hit.color}:${hit.id}`
                      );

                      return (
                        <Link
                          href={`/veiculo/${detailsVehiclePath}`}
                          key={index + hit.version + hit.id}
                          className={`flex items-center border-b border-gray-200 p-3 hover:bg-gray-100 cursor-pointer ${index === selectedIndex ? "bg-gray-200" : ""} ${isFirst ? "rounded-t-2xl" : ""} ${isLast ? "rounded-b-2xl" : ""}`}
                          onMouseEnter={() => setSelectedIndex(index)}
                          onClick={() => handlePostHogEvent(hit)}
                        >
                          <div className="w-20 h-20 mr-4 flex-none md:w-16 md:h-16 relative">
                            {hit.images && (
                              <Image
                                src={hit.images[0]}
                                alt={`${hit.make} ${hit.model}`}
                                className="w-full h-full object-cover rounded-md"
                                fill
                                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                              />
                            )}
                          </div>
                          <div className="w-full flex flex-col items-start text-start">
                            <h4 className="text-md md:text-lg font-semibold">
                              {hit.make} {hit.model}
                            </h4>
                            <p className="text-sm md:text-md">{hit.version}</p>
                            <p className="text-sm text-gray-600">
                              {hit.modelYear ?? ""} - {hit.color ?? ""} -{" "}
                              {hit.transmission ?? ""}
                            </p>
                          </div>
                          <div className="flex-none text-right text-lg font-semibold hidden md:block">
                            {(hit.price ?? 0).toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="w-full flex items-center justify-center -mt-10">
              <figure
                className={`absolute z-0 [@media_(max-width:500px)]:-bottom-[23%] -bottom-[190px] sm:-bottom-[265px] lg:-bottom-[${pathname.includes("/motos") ? "300px" : "270px"}] sm:block m-auto w-full flex justify-center`}
              >
                <Image
                  alt="Banner Vehicle"
                  width={900}
                  height={0}
                  decoding="async"
                  src={image}
                  priority
                  className="md:block max-w-full h-auto  object-contain mx-auto transition duration-300 text-transparent"
                />
              </figure>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
